import { useEffect, useState } from "react";

import { DateTime } from "luxon";

import useLocalStorage from "./useLocalStorage";

const DEFAULT_OPTIONS = {
  enabled: true,
  staleTimeInSeconds: 86400,
};

/**
 *
 * @param key - Unique name for this local storage timeout
 * @param options - enabled - A boolean indicating whether the hook should run
 * @param options - staleTimeInSeconds - Time in seconds (default 24hrs) until the value becomes stale and the date should be reset
 * @returns A tuple with a boolean indicating the current date has exceeded the timeout & a fn to reset the timeout to the current date
 */
const useLocalStorageTimeout = (
  key: string,
  options?: { enabled?: boolean; staleTimeInSeconds?: number }
): [valueExceedsTimeout: boolean, resetTimeout: () => void] => {
  const currentTime = DateTime.now();
  const enabled = options?.enabled ?? DEFAULT_OPTIONS.enabled;
  const staleTimeInSeconds =
    options?.staleTimeInSeconds ?? DEFAULT_OPTIONS.staleTimeInSeconds;

  const [valueExceedsTimeout, setValueExceedsTimeout] = useState(false);
  const [localStorageValue, setLocalStorageValue] = useLocalStorage<
    string | null
  >(key, null);

  const resetTimeout = () => {
    if (!enabled) {
      return;
    }

    setLocalStorageValue(currentTime.toISO());
    setValueExceedsTimeout(false);
  };

  useEffect(() => {
    if (!enabled) {
      setValueExceedsTimeout(false);
      return;
    }

    if (!localStorageValue) {
      setValueExceedsTimeout(true);
      return;
    }

    if (
      Math.round(
        currentTime.diff(DateTime.fromISO(localStorageValue)).as("seconds")
      ) >= staleTimeInSeconds
    ) {
      setValueExceedsTimeout(true);
      setLocalStorageValue(currentTime.toISO());
    }
  }, [key, localStorageValue, enabled]);

  return [valueExceedsTimeout, resetTimeout];
};

export default useLocalStorageTimeout;
